// src/pages/finance-portal/journal-entries/JournalEntries.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card, CardContent,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, IconButton, Alert, CircularProgress, Skeleton,
    MenuItem, Select, FormControl, InputLabel, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment,
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PublishIcon from '@mui/icons-material/Publish';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import JournalEntryForm from './JournalEntryForm';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    DRAFT: { bg: '#fef3c7', color: '#92400e' },
    POSTED: { bg: '#dcfce7', color: '#166534' },
    VOIDED: { bg: '#fecaca', color: '#991b1b' },
};

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

export default function JournalEntries() {
    const [entries, setEntries] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');
    const [search, setSearch] = useState('');

    // Form dialog
    const [formOpen, setFormOpen] = useState(false);
    const [editEntry, setEditEntry] = useState(null);

    // View dialog
    const [viewEntry, setViewEntry] = useState(null);

    // Delete dialog
    const [deleteDialog, setDeleteDialog] = useState({ open: false, entry: null });
    const [deleting, setDeleting] = useState(false);

    const fetchEntries = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            if (statusFilter) params.status = statusFilter;
            if (search) params.search = search;

            const res = await axiosInstance.get('/api/finance/journal-entries', { params });
            if (res.data.success) {
                setEntries(res.data.data);
                setTotal(res.data.total);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load journal entries');
        } finally {
            setIsLoading(false);
        }
    }, [page, rowsPerPage, statusFilter, search]);

    useEffect(() => { fetchEntries(); }, [fetchEntries]);

    const handlePost = async (entryId) => {
        try {
            await axiosInstance.post(`/api/finance/journal-entries/${entryId}/post`, {});
            fetchEntries();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to post entry');
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await axiosInstance.delete(`/api/finance/journal-entries/${deleteDialog.entry.id}`);
            setDeleteDialog({ open: false, entry: null });
            fetchEntries();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete entry');
        } finally {
            setDeleting(false);
        }
    };

    const handleView = async (entryId) => {
        try {
            const res = await axiosInstance.get(`/api/finance/journal-entries/${entryId}`);
            if (res.data.success) setViewEntry(res.data.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load entry');
        }
    };

    const handleEdit = async (entryId) => {
        try {
            const res = await axiosInstance.get(`/api/finance/journal-entries/${entryId}`);
            if (res.data.success) {
                setEditEntry(res.data.data);
                setFormOpen(true);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load entry');
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <MenuBookIcon sx={{ fontSize: 28, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Journal Entries</Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Double-entry bookkeeping</Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* Toolbar */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField size="small" placeholder="Search..." value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>, sx: { fontFamily, borderRadius: '10px' } }}
                        sx={{ minWidth: { xs: '100%', sm: 200 } }}
                    />
                    <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 130 } }}>
                        <InputLabel sx={{ fontFamily }}>Status</InputLabel>
                        <Select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(0); }} label="Status" sx={{ fontFamily, borderRadius: '10px' }}>
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="DRAFT">Draft</MenuItem>
                            <MenuItem value="POSTED">Posted</MenuItem>
                            <MenuItem value="VOIDED">Voided</MenuItem>
                        </Select>
                    </FormControl>
                    <Box sx={{ flex: 1 }} />
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditEntry(null); setFormOpen(true); }}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        New Journal Entry
                    </Button>
                </Box>

                {/* Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Entry #', 'Date', 'Description', 'Status', 'Total Debit', 'Total Credit', 'Actions'].map(h => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4, 5].map(i => <TableRow key={i}><TableCell colSpan={7}><Skeleton height={36} /></TableCell></TableRow>)
                                ) : entries.length === 0 ? (
                                    <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography sx={{ fontFamily, color: '#94a3b8' }}>No journal entries found</Typography>
                                    </TableCell></TableRow>
                                ) : entries.map(entry => {
                                    const statusStyle = STATUS_COLORS[entry.status] || {};
                                    return (
                                        <TableRow key={entry.id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>{entry.entry_number}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{entry.entry_date}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.description || '—'}</TableCell>
                                            <TableCell>
                                                <Chip label={entry.status} size="small" sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22, bgcolor: statusStyle.bg, color: statusStyle.color }} />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600 }}>{formatCurrency(entry.total_debit)}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600 }}>{formatCurrency(entry.total_credit)}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <Tooltip title="View"><IconButton size="small" onClick={() => handleView(entry.id)} sx={{ color: '#94a3b8' }}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                                                    {entry.status === 'DRAFT' && (
                                                        <>
                                                            <Tooltip title="Edit"><IconButton size="small" onClick={() => handleEdit(entry.id)} sx={{ color: '#94a3b8' }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                                            <Tooltip title="Post"><IconButton size="small" onClick={() => handlePost(entry.id)} sx={{ color: '#94a3b8', '&:hover': { color: ACCENT } }}><PublishIcon fontSize="small" /></IconButton></Tooltip>
                                                            <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleteDialog({ open: true, entry })} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                                        </>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination component="div" count={total} page={page} onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage} onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily } }}
                    />
                </Card>
            </Box>

            {/* Entry Form Dialog */}
            <JournalEntryForm open={formOpen} onClose={() => { setFormOpen(false); setEditEntry(null); }} onSaved={fetchEntries} editEntry={editEntry} />

            {/* View Dialog */}
            <Dialog open={!!viewEntry} onClose={() => setViewEntry(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    {viewEntry?.entry_number} — {viewEntry?.entry_date}
                    <Chip label={viewEntry?.status} size="small" sx={{ ml: 2, fontFamily, fontSize: '0.7rem', fontWeight: 600, ...(STATUS_COLORS[viewEntry?.status] || {}) }} />
                </DialogTitle>
                <DialogContent>
                    {viewEntry?.description && <Typography sx={{ fontFamily, color: '#64748b', mb: 2 }}>{viewEntry.description}</Typography>}
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                <TableCell sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b' }}>Account</TableCell>
                                <TableCell sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b' }}>Memo</TableCell>
                                <TableCell align="right" sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b' }}>Debit</TableCell>
                                <TableCell align="right" sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b' }}>Credit</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {viewEntry?.lines?.map((line, i) => (
                                <TableRow key={i}>
                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem' }}>{line.account_number} — {line.account_name}</TableCell>
                                    <TableCell sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>{line.memo || '—'}</TableCell>
                                    <TableCell align="right" sx={{ fontFamily, fontSize: '0.85rem', fontWeight: line.debit_amount ? 600 : 400 }}>{line.debit_amount ? formatCurrency(line.debit_amount) : ''}</TableCell>
                                    <TableCell align="right" sx={{ fontFamily, fontSize: '0.85rem', fontWeight: line.credit_amount ? 600 : 400 }}>{line.credit_amount ? formatCurrency(line.credit_amount) : ''}</TableCell>
                                </TableRow>
                            ))}
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                <TableCell colSpan={2} sx={{ fontFamily, fontWeight: 700 }}>Totals</TableCell>
                                <TableCell align="right" sx={{ fontFamily, fontWeight: 700 }}>{formatCurrency(viewEntry?.total_debit)}</TableCell>
                                <TableCell align="right" sx={{ fontFamily, fontWeight: 700 }}>{formatCurrency(viewEntry?.total_credit)}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setViewEntry(null)} sx={{ fontFamily, textTransform: 'none' }}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, entry: null })} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Delete {deleteDialog.entry?.entry_number}?</DialogTitle>
                <DialogContent><Typography sx={{ fontFamily, color: '#64748b' }}>This will permanently delete the draft journal entry and all its lines.</Typography></DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteDialog({ open: false, entry: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleDelete} disabled={deleting}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
                    >{deleting ? 'Deleting...' : 'Delete'}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
